// frontend/src/core/components/DebugDrawer.tsx
import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../apiClient.ts';

interface ApiLog {
  id: number;
  method: string;
  url: string;
  request_params: string | null;
  response_body: string | null;
  status_code: number;
  created_at: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  // An optional filter text, e.g. "published_posts" for the Posts tab
  filterText?: string;
  filterFn?: (log: ApiLog) => boolean;
}

export function DebugDrawer({ isOpen, onClose, filterText, filterFn }: Props) {
  const [logs, setLogs] = useState<ApiLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.get<ApiLog[]>('/api/debug/logs');
      setLogs(data);
    } catch (e) {
      console.error('Failed to fetch debug logs', e);
    } finally {
      setLoading(false);
    }
  }, []);

  const clearLogs = async () => {
    try {
      await api.delete('/api/debug/logs');
      setLogs([]);
    } catch (e) {
      console.error('Failed to clear logs', e);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchLogs();
    }
  }, [isOpen, fetchLogs]);

  useEffect(() => {
    if (isOpen && autoRefresh) {
      const interval = setInterval(fetchLogs, 5000);
      return () => clearInterval(interval);
    }
  }, [isOpen, autoRefresh, fetchLogs]);

  const filteredLogs = filterFn 
    ? logs.filter(filterFn)
    : filterText 
      ? logs.filter(l => {
          const filters = filterText.split('|');
          return filters.some(f => l.url.includes(f) || (l.request_params && l.request_params.includes(f)));
        })
      : logs;

  const groupedLogs = groupLogsByTime(filteredLogs);

  const copyGroupToClipboard = (group: LogGroup, e: React.MouseEvent) => {
    e.stopPropagation();
    const text = group.logs.map(log => {
      let res = `--- [${new Date(log.created_at).toLocaleTimeString('uk-UA')}] ${log.method} ${log.status_code} ---\n`;
      res += `URL: ${log.url}\n`;
      if (log.request_params) res += `Request: ${log.request_params}\n`;
      res += `Response:\n${formatJsonIfPossible(log.response_body)}\n`;
      return res;
    }).join('\n');
    navigator.clipboard.writeText(text);
    // Optional: show a toast or change icon briefly
    const target = e.target as HTMLButtonElement;
    const oldText = target.innerText;
    target.innerText = 'Скопійовано!';
    setTimeout(() => { target.innerText = oldText; }, 2000);
  };

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div 
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.3)',
            zIndex: 999
          }}
          onClick={onClose}
        />
      )}
      
      {/* Drawer */}
      <div 
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: '500px',
          maxWidth: '100vw',
          backgroundColor: 'var(--color-surface)',
          borderLeft: '1px solid var(--color-border)',
          boxShadow: '-4px 0 24px rgba(0,0,0,0.5)',
          zIndex: 1000,
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.3s ease-in-out',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div style={{ padding: 'var(--space-4)', borderBottom: '1px solid var(--color-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: '1.25rem' }}>FB API Logs {loading && <span style={{fontSize: '0.8rem', color: 'var(--color-primary)'}}>(Оновлення...)</span>}</h2>
          <button className="btn btn--secondary" onClick={onClose}>Закрити</button>
        </div>

        <div style={{ padding: 'var(--space-3)', borderBottom: '1px solid var(--color-border)', display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', alignItems: 'center' }}>
          <label style={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} />
            Авто-оновлення (5с)
          </label>
          <button className="btn btn--secondary" style={{ padding: '2px 8px', fontSize: '0.75rem' }} onClick={fetchLogs}>Оновити</button>
          <button className="btn btn--secondary" style={{ padding: '2px 8px', fontSize: '0.75rem', color: 'var(--color-error)', borderColor: 'var(--color-error)' }} onClick={clearLogs}>Очистити</button>
        </div>

        {filterText && (
          <div style={{ padding: 'var(--space-2) var(--space-3)', background: 'var(--color-surface-2)', fontSize: '0.8rem' }}>
            Фільтр для поточної вкладки: <strong>{filterText}</strong>
          </div>
        )}

        <div style={{ flex: 1, overflowY: 'auto', padding: 'var(--space-3)' }}>
          {groupedLogs.length === 0 ? (
            <div className="text-muted text-center" style={{ marginTop: 'var(--space-5)' }}>
              Немає записів{filterText ? ' за цим фільтром' : ''}.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
              {groupedLogs.map((group, gIdx) => (
                <div key={group.id} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 var(--space-2)' }}>
                    <span style={{ fontSize: '0.85rem', fontWeight: 'bold', color: 'var(--color-primary)' }}>
                      Дія о {new Date(group.timestamp).toLocaleTimeString('uk-UA')} ({group.logs.length} зап.)
                    </span>
                    <button 
                      className="btn btn--secondary" 
                      style={{ padding: '2px 8px', fontSize: '0.7rem' }}
                      onClick={(e) => copyGroupToClipboard(group, e)}
                    >
                      Копіювати текст
                    </button>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                    {group.logs.map(log => (
                      <details 
                        key={log.id} 
                        style={{ 
                          background: 'var(--color-surface-2)', 
                          borderRadius: 'var(--radius-sm)', 
                          border: '1px solid var(--color-border)',
                          overflow: 'hidden'
                        }}
                        open={gIdx === 0} // Auto-open the most recent group's items
                      >
                        <summary style={{ padding: 'var(--space-2)', cursor: 'pointer', display: 'flex', gap: 'var(--space-2)', alignItems: 'center', userSelect: 'none' }}>
                          <span className={`badge ${log.status_code >= 400 ? 'badge--expired' : 'badge--active'}`}>
                            {log.status_code}
                          </span>
                          <span style={{ fontWeight: 'bold', fontSize: '0.75rem' }}>{log.method}</span>
                          <span style={{ fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }} title={log.url}>
                            {log.url.split('?')[0].split('.com/')[1] || log.url}
                          </span>
                          <span style={{ fontSize: '0.65rem', color: 'var(--color-text-subtle)' }}>
                            {new Date(log.created_at).toLocaleTimeString('uk-UA')}
                          </span>
                        </summary>
                        
                        <div style={{ padding: 'var(--space-2)', borderTop: '1px solid var(--color-border)', fontSize: '0.8rem', background: '#0d1117' }}>
                          <div style={{ marginBottom: '8px', wordBreak: 'break-all', color: '#8b949e' }}>
                            <strong>URL:</strong> {log.url}
                          </div>
                          {log.request_params && (
                            <div style={{ marginBottom: '8px' }}>
                              <strong>Request Body:</strong>
                              <pre style={{ margin: 0, padding: '8px', background: '#161b22', borderRadius: '4px', overflowX: 'auto', color: '#c9d1d9' }}>
                                {log.request_params}
                              </pre>
                            </div>
                          )}
                          <div>
                            <strong>Response Body:</strong>
                            <pre style={{ margin: 0, padding: '8px', background: '#161b22', borderRadius: '4px', overflowX: 'auto', color: '#c9d1d9', maxHeight: '300px' }}>
                              {formatJsonIfPossible(log.response_body)}
                            </pre>
                          </div>
                        </div>
                      </details>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function formatJsonIfPossible(str: string | null) {
  if (!str) return 'null';
  try {
    return JSON.stringify(JSON.parse(str), null, 2);
  } catch {
    return str;
  }
}

interface LogGroup {
  id: string;
  timestamp: string;
  logs: ApiLog[];
}

function groupLogsByTime(logs: ApiLog[], maxDiffMs = 5000): LogGroup[] {
  if (logs.length === 0) return [];
  
  const groups: LogGroup[] = [];
  let currentGroup: LogGroup = {
    id: `grp_${logs[0].id}`,
    timestamp: logs[0].created_at,
    logs: [logs[0]]
  };
  
  for (let i = 1; i < logs.length; i++) {
    const log = logs[i];
    const prevLog = currentGroup.logs[currentGroup.logs.length - 1]; // oldest in current group so far
    
    // Both are ISO strings. Array is sorted DESC (newest first).
    const diff = new Date(prevLog.created_at).getTime() - new Date(log.created_at).getTime();
    
    if (diff <= maxDiffMs) {
      currentGroup.logs.push(log);
    } else {
      groups.push(currentGroup);
      currentGroup = {
        id: `grp_${log.id}`,
        timestamp: log.created_at,
        logs: [log]
      };
    }
  }
  groups.push(currentGroup);
  return groups;
}
